import { useState } from "react";
import { Link } from "wouter";
import { useListRegistrations, RegistrationStatus } from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDate } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Building2, User, Send, MessageSquare } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { useDebounce } from "@/hooks/use-debounce";

function ReplyIndicator({ lastReplyAt }: { lastReplyAt?: string }) {
  if (!lastReplyAt) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="relative inline-flex items-center justify-center text-blue-600 dark:text-blue-400"
            aria-label={`Reply received ${formatDate(lastReplyAt) ?? ""}`}
            data-testid="reply-indicator"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-background" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Reply received {formatDate(lastReplyAt)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function RegistrationsList() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [status, setStatus] = useState<string>("all");
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [supplier, setSupplier] = useState("");
  const debouncedSupplier = useDebounce(supplier, 500);
  const [sentFrom, setSentFrom] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [hasReplyOnly, setHasReplyOnly] = useState(false);

  const { data: regsData, isLoading } = useListRegistrations({
    q: debouncedSearch || undefined,
    status: status !== "all" ? status : undefined,
    supplierContactId: debouncedSupplier || undefined,
    sentFrom: sentFrom ? new Date(sentFrom).toISOString() : undefined,
    sentTo: sentTo ? new Date(sentTo).toISOString() : undefined,
    scope,
  });

  const filteredResults = regsData?.results?.filter(
    (reg) => !hasReplyOnly || !!reg.lastReplyAt,
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Registrations</h1>
          <p className="text-muted-foreground">Track all supplier deal registrations.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center w-full max-w-sm relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search account or opportunity..." 
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.values(RegistrationStatus).map(s => (
                <SelectItem key={s} value={s}>{s.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={scope} onValueChange={(val: "mine" | "all") => setScope(val)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">My Deals</SelectItem>
              <SelectItem value="all">All Deals</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant={hasReplyOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setHasReplyOnly((v) => !v)}
            className="gap-1.5"
            data-testid="toggle-has-reply"
            aria-pressed={hasReplyOnly}
          >
            <MessageSquare className="h-4 w-4" />
            Has reply
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Input
          placeholder="Supplier contact id (sf id)"
          className="w-full sm:max-w-xs"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-muted-foreground shrink-0">Sent from</label>
          <Input
            type="date"
            className="w-[150px] min-w-0"
            value={sentFrom}
            onChange={(e) => setSentFrom(e.target.value)}
          />
          <label className="text-sm text-muted-foreground shrink-0">to</label>
          <Input
            type="date"
            className="w-[150px] min-w-0"
            value={sentTo}
            onChange={(e) => setSentTo(e.target.value)}
          />
          {(sentFrom || sentTo || supplier) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSentFrom("");
                setSentTo("");
                setSupplier("");
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Mobile card layout */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="border rounded-xl bg-card p-4 space-y-3">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))
        ) : filteredResults && filteredResults.length > 0 ? (
          filteredResults.map((reg) => (
            <Link key={reg.id} href={`/registrations/${reg.id}`}>
              <div className="border rounded-xl bg-card p-4 space-y-3 hover:bg-muted/50 transition-colors cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className="truncate">{reg.dealOpportunityName || "—"}</span>
                      <ReplyIndicator lastReplyAt={reg.lastReplyAt} />
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Building2 className="h-3 w-3 shrink-0" />
                      <span className="truncate">{reg.dealAccountName || "—"}</span>
                    </div>
                  </div>
                  <StatusBadge status={reg.status} />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Supplier</div>
                  <div className="font-medium">{reg.supplierAccountName || "—"}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                    <User className="h-3 w-3" />
                    {reg.supplierContactName || "—"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Technology</div>
                    {reg.technology ? (
                      <div className="inline-flex items-center px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs font-medium border mt-0.5">
                        {reg.technology}
                      </div>
                    ) : <div>—</div>}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Sent At</div>
                    <div>{formatDate(reg.sentAt) || "—"}</div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full">View</Button>
              </div>
            </Link>
          ))
        ) : (
          <div className="border rounded-xl bg-card p-8 flex flex-col items-center justify-center text-muted-foreground">
            <Send className="h-8 w-8 mb-2 opacity-20" />
            <p>No registrations found.</p>
            {(search || status !== "all" || hasReplyOnly) && <p className="text-sm mt-1">Try clearing your filters.</p>}
          </div>
        )}
      </div>

      <div className="hidden sm:block border rounded-xl bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Opportunity / Account</TableHead>
              <TableHead>Supplier / Contact</TableHead>
              <TableHead>Technology</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-10 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-10 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : filteredResults && filteredResults.length > 0 ? (
              filteredResults.map((reg) => (
                <TableRow key={reg.id} className="hover:bg-muted/50 group transition-colors">
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground flex items-center gap-2">
                        <span>{reg.dealOpportunityName || "—"}</span>
                        <ReplyIndicator lastReplyAt={reg.lastReplyAt} />
                      </span>
                      <span className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Building2 className="h-3 w-3" />
                        {reg.dealAccountName || "—"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{reg.supplierAccountName || "—"}</span>
                      <span className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <User className="h-3 w-3" />
                        {reg.supplierContactName || "—"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {reg.technology ? (
                      <div className="inline-flex items-center px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs font-medium border">
                        {reg.technology}
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={reg.status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(reg.sentAt) || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link href={`/registrations/${reg.id}`}>
                      <Button variant="ghost" size="sm" className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-48 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Send className="h-8 w-8 mb-2 opacity-20" />
                    <p>No registrations found.</p>
                    {(search || status !== "all" || hasReplyOnly) && <p className="text-sm mt-1">Try clearing your filters.</p>}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
